<script setup lang="tsx">
import type { CustomFieldDefinition, User, Response } from '@/models/api-generated'
import { CF_Calendar, CF_Checkbox, CF_Select, CF_User } from '@/models/enums-generated'
import http from '@/services/http'
import { onMounted, ref, watch } from 'vue'
import UserSelect from '../widgets/UserSelect.vue'
import Datepicker from '@vuepic/vue-datepicker'
import { formatLocale, getDateFormat } from '@/services/utils'
import { useTheme } from '../user/useTheme'

const { theme } = useTheme()
const model = defineModel<string>()
const props = defineProps<{
	field: CustomFieldDefinition
	class?: string
}>()

const user = ref<User | undefined>()

function fetchUser() {
	if (model.value) {
		const userId = Number.parseInt(model.value)
		http.get<Response<User>>(`api/user/${userId}`).then((a) => (user.value = a.data.data))
	}
}

onMounted(() => {
	if (props.field.type === CF_User) {
		watch(user, () => (model.value = user.value?.id.toString() ?? ''))
		watch(model, fetchUser)
		fetchUser()
	}
})
</script>

<template>
	<template v-if="props.field.type === CF_Select">
		<select class="form-select" :class="props.class" v-model="model">
			<option>-</option>
			<option v-for="v in props.field.values" :key="v" :value="v">{{ v }}</option>
		</select>
	</template>
	<template v-else-if="props.field.type === CF_Checkbox">
		<div class="form-check form-switch form-switch-md">
			<input class="form-check-input" type="checkbox" v-model="model" true-value="1" false-value="" />
		</div>
	</template>

	<template v-else-if="props.field.type === CF_Calendar">
		<Datepicker
			v-model="model"
			:clearable="true"
			:teleport="true"
			:enable-time-picker="false"
			:format="getDateFormat()"
			:format-locale="formatLocale()"
			:select-text="$t('misc.select')"
			:cancel-text="$t('misc.cancel')"
			text-input
			class="input-box"
			:class="props.class"
			:dark="theme === 'dark'"
		/>
	</template>

	<template v-else-if="props.field.type === CF_User">
		<UserSelect v-model="user" :class="props.class"></UserSelect>
	</template>

	<template v-else>
		<input type="text" class="form-control" :class="props.class" v-model="model" maxlength="500" />
	</template>
</template>
